import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CInput,
  CInputGroup,
  CInputGroupText,
  CPagination,
  CRow,
} from "@coreui/react";
import { useCallback, useEffect, useState } from "react";
import { BsFillPlusCircleFill } from "react-icons/bs";
import { useSelector } from "react-redux";
import { getCatalogItemList } from "../../api/catalog-items";
import {
  INVOICES_CREATE,
  INVOICES_UPDATE,
} from "../../auth/permissions";
import CatalogItem from "../../models/catalog-item";
import { IVA_TYPE_CHOICES } from "../../models/iva";
import {
  findMeasureUnit,
  measureUnitLabel,
} from "../../models/measure-unit";
import { RootState } from "../../store";
import { SUCCESS } from "../../utils/constants/tags";
import HasAnyPermissionComponent from "../permissions/HasAnyPermissionComponent";
import { successAlert, warningAlert } from "../utils/messages";
import CatalogItemCrudActions from "./CatalogItemCrudActions";
import CatalogItemDeleteModal from "./CatalogItemDeleteModal";
import CatalogItemUpdateModal from "./CatalogItemUpdateModal";

const ITEMS_PER_PAGE = 10;

const MANAGE = [INVOICES_CREATE, INVOICES_UPDATE];

const CatalogItemCrud = () => {
  const fields = [
    { key: "description", _classes: ["text-center"], label: "Descripción" },
    { key: "code", _classes: ["text-center"], label: "Código" },
    { key: "unitPrice", _classes: ["text-center"], label: "Precio unitario" },
    { key: "ivaType", _classes: ["text-center"], label: "Tipo IVA" },
    {
      key: "measureUnit",
      _classes: ["text-center"],
      label: "Unidad de medida",
    },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [catalogItem, setCatalogItem] = useState<CatalogItem | undefined>(
    undefined,
  );

  const [loading, setLoading] = useState(true);
  const [catalogItems, setCatalogItems] = useState<CatalogItem[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const fetchCatalogItems = useCallback(
    async (page: number, searchTerm: string) => {
      setLoading(true);

      const status = await getCatalogItemList(
        taxPayer.id!,
        searchTerm,
        ITEMS_PER_PAGE,
        ITEMS_PER_PAGE * (page - 1),
      );

      if (status.status === SUCCESS && status.data !== undefined) {
        setCatalogItems(status.data.items);
        setTotalPages(Math.ceil((status.data.count ?? 0) / ITEMS_PER_PAGE));
        setCurrentPage(page);
        setAppliedSearch(searchTerm);
      } else {
        warningAlert(status.detail ?? "Error desconocido");
      }

      setLoading(false);
    },
    [taxPayer],
  );

  const onPageChange = (page: number) => {
    if (page !== currentPage) {
      fetchCatalogItems(page, appliedSearch);
    }
  };

  const onSearch = () => {
    fetchCatalogItems(1, search.trim());
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onSearch();
    }
  };

  const onAddClick = () => {
    setCatalogItem(undefined);
    setShowEditModal(true);
  };

  const onUpdateClick = (item: CatalogItem) => {
    setCatalogItem(item);
    setShowEditModal(true);
  };

  const onDeleteClick = (item: CatalogItem) => {
    setCatalogItem(item);
    setShowDeleteModal(true);
  };

  const onEditCancel = () => {
    setShowEditModal(false);
    setCatalogItem(undefined);
  };

  const onEditSuccess = (created?: CatalogItem) => {
    setShowEditModal(false);
    setCatalogItem(undefined);

    // How many already issued lines the entry just claimed. Worth saying: it is
    // the difference between a catalogue that describes what was invoiced and
    // one that only starts counting from today.
    const linked = created?.linkedItems ?? 0;

    successAlert(
      linked > 0
        ? `Ítem guardado. Se vincularon ${linked} líneas ya facturadas.`
        : "Ítem guardado con éxito!",
    );

    fetchCatalogItems(currentPage, appliedSearch);
  };

  const onDeleteCancel = () => {
    setShowDeleteModal(false);
    setCatalogItem(undefined);
  };

  const onDeleteSuccess = () => {
    setShowDeleteModal(false);
    setCatalogItem(undefined);
    successAlert("Ítem desactivado con éxito!");
    fetchCatalogItems(currentPage, appliedSearch);
  };

  useEffect(() => {
    fetchCatalogItems(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer]);

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CAlert color="info" className="d-flex align-items-center">
            <i className="fa fa-info-circle mr-2 flex-shrink-0"></i>
            <span>
              Registrá tus productos y servicios para cargar tus facturas más
              rápido. Podés guardar un precio sugerido y cambiarlo en cada
              factura.
            </span>
          </CAlert>
        </CCol>
      </CRow>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2">
                <h3>Productos y servicios</h3>
              </div>
              <div className="p-2 ml-auto">
                <HasAnyPermissionComponent permissionNames={MANAGE}>
                  <CButton color="primary" onClick={onAddClick}>
                    <BsFillPlusCircleFill />
                    &nbsp; Nuevo
                  </CButton>
                </HasAnyPermissionComponent>
              </div>
            </CCardHeader>
            <CCardBody>
              <CRow className="mb-4">
                <CCol>
                  <CInputGroup>
                    <CInputGroupText>
                      <i className="fa fa-search"></i>
                    </CInputGroupText>
                    <CInput
                      type="text"
                      placeholder="Buscar por descripción o código"
                      value={search}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setSearch(e.target.value)
                      }
                      onKeyDown={onSearchKeyDown}
                    />
                    <CButton
                      type="button"
                      color="primary"
                      variant="outline"
                      onClick={onSearch}
                    >
                      Buscar
                    </CButton>
                  </CInputGroup>
                </CCol>
              </CRow>
              <div>
                <CDataTable
                  noItemsView={{
                    noItems: "Todavía no cargaste productos ni servicios",
                    noResults: "No se encontraron productos ni servicios",
                  }}
                  addTableClasses={"table-fixed"}
                  fields={fields}
                  items={catalogItems}
                  striped
                  border
                  loading={loading}
                  responsive
                  scopedSlots={{
                    description: (item: CatalogItem) => (
                      <td className="text-center">{item.description}</td>
                    ),
                    code: (item: CatalogItem) => (
                      <td className="text-center">
                        {item.code ? <code>{item.code}</code> : "-"}
                      </td>
                    ),
                    unitPrice: (item: CatalogItem) => (
                      <td className="text-center">
                        {item.unitPrice == null
                          ? "-"
                          : `${Number(item.unitPrice).toLocaleString("es-PY")} ${item.priceCurrency ?? "PYG"}`}
                      </td>
                    ),
                    ivaType: (item: CatalogItem) => (
                      <td className="text-center">
                        {item.ivaType
                          ? (IVA_TYPE_CHOICES.get(item.ivaType) ?? item.ivaType)
                          : "-"}
                      </td>
                    ),
                    measureUnit: (item: CatalogItem) => (
                      <td className="text-center">
                        {item.measureUnit !== undefined &&
                        item.measureUnit !== null
                          ? measureUnitLabel(findMeasureUnit(item.measureUnit))
                          : "-"}
                      </td>
                    ),
                    actions: (item: CatalogItem) => (
                      <CatalogItemCrudActions
                        catalogItem={item}
                        onUpdateClick={onUpdateClick}
                        onDeleteClick={onDeleteClick}
                      />
                    ),
                  }}
                />
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <CatalogItemUpdateModal
        show={showEditModal}
        catalogItem={catalogItem}
        onCancel={onEditCancel}
        onSuccess={onEditSuccess}
      />
      <CatalogItemDeleteModal
        show={showDeleteModal}
        catalogItem={catalogItem}
        onCancel={onDeleteCancel}
        onSuccess={onDeleteSuccess}
      />
    </>
  );
};

export default CatalogItemCrud;
